import React, { useState, useEffect } from "react";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Button,
  Center,
  Flex,
  SimpleGrid,
  Skeleton,
  Spinner,
  Text,
  useCheckboxGroup,
  useMediaQuery,
  Badge,
  Icon,
} from "@chakra-ui/react";
import { ChevronRightIcon, ChevronLeftIcon } from "@chakra-ui/icons";
import { FaBook, FaFilter, FaRedo } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useParams, Link } from "react-router-dom";
import InfiniteScroll from "react-infinite-scroll-component";

import ProductItem from "./ProductItem";
import LeftSidebar from "./LeftSidebar";
import MiniFilter from "./Filter/MiniFilter";
import { getProductError, getProductLoading, getProductsSuccess } from "../../Redux/Products/action";
import { apiUrl } from "../ApiUrl/ApiUrl";

const Products = () => {
  const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
  const [selectedLetter, setSelectedLetter] = useState("");
  const [selectedGender, setSelectedGender] = useState("");
  const [isLargerThan768] = useMediaQuery("(min-width: 768px)");

  const [page, setPage] = useState(1);
  const perPagelimitProduct = 16;
  const [total, setTotal] = useState(0);

  const dispatch = useDispatch();
  const { loading, error, products = [] } = useSelector((state) => state);

  const [priceRange, setPriceRange] = useState([]);
  const { value, getCheckboxProps } = useCheckboxGroup();
  const location = useLocation();

  const params = new URLSearchParams(location.search);
  const maincat = params.get("maincat");
  const keyword = params.get("keyword");
  const subcat = params.get("subcat");

  useEffect(() => {
    if (subcat === "none") {
      localStorage.removeItem("subcatname");
    }
    const currentSubcat = params.get("subcatname");
    if (currentSubcat) {
      localStorage.setItem("subcatname", currentSubcat);
    }
  }, [location.search]);

  const handleGenderChange = (gender) => {
    setSelectedGender(gender);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchData();
  }, [location.search, selectedLetter, selectedGender, page]);

  const fetchData = async () => {
    dispatch(getProductLoading());

    const searchParams = new URLSearchParams(location.search);
    const subcname = searchParams.get("maincat");
    const searchKeyword = searchParams.get("keyword") || searchParams.get("search");
    const subcatname = searchKeyword ? null : localStorage.getItem("subcatname");

    let url;

    if (searchKeyword) {
      localStorage.removeItem("subcatname");
      url = `${apiUrl}/public/search?keyword=${encodeURIComponent(searchKeyword)}`;
      if (selectedLetter) url += `&letter=${encodeURIComponent(selectedLetter)}`;
    } else if (subcatname) {
      url = `${apiUrl}/public/productsBysubcatname?subcatname=${encodeURIComponent(subcatname)}`;
      if (selectedLetter) url += `&letter=${encodeURIComponent(selectedLetter)}`;
      if (selectedGender) url += `&gender=${encodeURIComponent(selectedGender)}`;
    } else if (subcname) {
      url = `${apiUrl}/public/getallbookbycatname?maincat=${encodeURIComponent(subcname)}`;
      if (selectedLetter) url += `&letter=${encodeURIComponent(selectedLetter)}`;
      if (selectedGender) url += `&gender=${encodeURIComponent(selectedGender)}`;
    } else {
      url = `${apiUrl}/public/allbooks`;
      if (selectedLetter) url += `?letter=${encodeURIComponent(selectedLetter)}`;
    }

    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error("Failed to fetch products");
      const data = await response.json();

      if (data.length > 0 && searchKeyword && data[0].maincatname) {
        localStorage.setItem("maincatname", data[0].maincatname);
      }

      const totalCount = response.headers.get("X-Total-Count") || data.length;
      setTotal(parseInt(totalCount, 10));
      dispatch(getProductsSuccess(data));
    } catch (err) {
      console.error("Error fetching products:", err);
      dispatch(getProductError());
    }
  };

  const currentCategoryTitle =
    localStorage.getItem("subcatname") ||
    maincat ||
    (keyword ? `Search: "${keyword}"` : "All Products");

  const totalPages = Math.ceil(total / perPagelimitProduct) || 1;

  const handleClearFilters = () => {
    setSelectedLetter("");
    setSelectedGender("");
    localStorage.removeItem("subcatname");
    window.location.reload();
  };

  return (
    <Box minH="85vh" bg="#F8FAFC" py={{ base: "3", md: "6" }} px={{ base: "3", md: "6", lg: "8" }}>
      <Box maxW="1360px" mx="auto">
        {/* Breadcrumb Bar */}
        <Flex justify="space-between" align="center" mb="4" wrap="wrap" gap="2">
          <Breadcrumb spacing="8px" separator={<ChevronRightIcon color="gray.400" />} fontSize="13px" color="gray.500">
            <BreadcrumbItem>
              <BreadcrumbLink as={Link} to="/">Home</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbItem>
              <BreadcrumbLink as={Link} to="/products?maincat=Books">Catalog</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbItem isCurrentPage>
              <BreadcrumbLink fontWeight="bold" color="#2563EB">
                {currentCategoryTitle}
              </BreadcrumbLink>
            </BreadcrumbItem>
          </Breadcrumb>

          <Text fontSize="13px" color="gray.500">
            Showing <Text as="span" fontWeight="bold" color="gray.900">{products.length}</Text> items
          </Text>
        </Flex>

        {/* Alphabet Navigation Scroller */}
        <Box
          bg="#FFFFFF"
          p="2.5"
          rounded="xl"
          border="1px solid #E2E8F0"
          shadow="sm"
          mb="5"
          overflowX="auto"
          css={{
            "&::-webkit-scrollbar": { height: "4px" },
            "&::-webkit-scrollbar-thumb": { background: "#E2E8F0", borderRadius: "2px" },
          }}
        >
          <Flex align="center" gap="1.5" minW="max-content">
            <Button
              size="xs"
              px="3"
              py="1"
              rounded="full"
              fontSize="12px"
              fontWeight="700"
              bg={!selectedLetter ? "#0F172A" : "gray.100"}
              color={!selectedLetter ? "white" : "gray.700"}
              _hover={{ bg: !selectedLetter ? "#1E293B" : "gray.200" }}
              onClick={() => setSelectedLetter("")}
            >
              All Titles
            </Button>
            {letters.map((letter) => {
              const isSelected = selectedLetter === letter;
              return (
                <Button
                  key={letter}
                  size="xs"
                  w="28px"
                  h="28px"
                  rounded="full"
                  fontSize="12px"
                  fontWeight="600"
                  p="0"
                  bg={isSelected ? "#EAB308" : "transparent"}
                  color={isSelected ? "gray.950" : "gray.600"}
                  _hover={{ bg: isSelected ? "#EAB308" : "gray.100", color: isSelected ? "gray.950" : "gray.900" }}
                  onClick={() => setSelectedLetter(isSelected ? "" : letter)}
                >
                  {letter}
                </Button>
              );
            })}
          </Flex>
        </Box>

        {/* Gender Filter (if Clothing category) */}
        {maincat === "Cloths-Accessories" && (
          <Flex align="center" gap="2" mb="5" bg="white" p="3" rounded="xl" border="1px solid #E2E8F0" shadow="sm">
            <Text fontSize="13px" fontWeight="bold" color="gray.700" mr="2">
              Gender:
            </Text>
            {["", "Male", "Female"].map((g) => (
              <Button
                key={g}
                size="sm"
                variant={selectedGender === g ? "solid" : "outline"}
                colorScheme={selectedGender === g ? "blue" : "gray"}
                rounded="full"
                fontSize="12px"
                onClick={() => handleGenderChange(g)}
              >
                {g === "" ? "All Genders" : g}
              </Button>
            ))}
          </Flex>
        )}

        {/* Main Grid & Sidebar Layout */}
        <Flex gap={{ base: 4, md: 6 }} align="flex-start">
          {/* Desktop Filter Sidebar */}
          {isLargerThan768 && (
            <Box position="sticky" top="80px" flexShrink={0}>
              <LeftSidebar
                getCheckboxProps={getCheckboxProps}
                setPriceRange={setPriceRange}
                priceRange={priceRange}
                value={value}
              />
            </Box>
          )}

          {/* Product Items Grid */}
          <Box flex="1" minW="0">
            {error ? (
              <Alert status="error" rounded="xl" shadow="sm">
                <AlertIcon />
                <Box>
                  <AlertTitle>Unable to load products</AlertTitle>
                  <AlertDescription fontSize="13px">
                    Please check your network connection or refresh the page.
                  </AlertDescription>
                </Box>
              </Alert>
            ) : loading ? (
              <SimpleGrid columns={{ base: 2, sm: 2, md: 3, lg: 4 }} spacing={{ base: 3, md: 4 }}>
                {Array.from({ length: 8 }).map((_, i) => (
                  <Skeleton key={i} height="360px" rounded="xl" />
                ))}
              </SimpleGrid>
            ) : products.length === 0 ? (
              <Box
                bg="white"
                rounded="2xl"
                p={{ base: "8", md: "16" }}
                textAlign="center"
                border="1px solid #E2E8F0"
                shadow="sm"
              >
                <Center mb="4">
                  <Box p="4" bg="gray.100" rounded="full">
                    <Icon as={FaBook} boxSize="36px" color="gray.400" />
                  </Box>
                </Center>
                <Text fontSize="20px" fontWeight="bold" color="gray.800" mb="2">
                  No products found
                </Text>
                <Text fontSize="14px" color="gray.500" maxW="400px" mx="auto" mb="6">
                  {selectedLetter
                    ? `No publications start with letter "${selectedLetter}". Try clearing the letter filter.`
                    : "No items match your active filter selection. Reset filters to view all available titles."}
                </Text>
                <Button
                  leftIcon={<Icon as={FaRedo} />}
                  bg="#0F172A"
                  color="white"
                  _hover={{ bg: "#1E293B" }}
                  onClick={handleClearFilters}
                  rounded="lg"
                  size="md"
                >
                  Reset All Filters
                </Button>
              </Box>
            ) : (
              <SimpleGrid columns={{ base: 2, sm: 2, md: 3, lg: 4 }} spacing={{ base: 3, md: 4 }}>
                {products.map((property, idx) => (
                  <ProductItem key={property.id || idx} property={property} i={idx} />
                ))}
              </SimpleGrid>
            )}

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <Flex justify="center" align="center" gap="2" mt="8" py="4">
                <Button
                  size="sm"
                  variant="outline"
                  leftIcon={<ChevronLeftIcon />}
                  isDisabled={page <= 1}
                  onClick={() => setPage(page - 1)}
                  rounded="lg"
                >
                  Previous
                </Button>

                <Flex gap="1">
                  {Array.from({ length: Math.min(totalPages, 5) }).map((_, i) => {
                    const pageNum = i + 1;
                    return (
                      <Button
                        key={pageNum}
                        size="sm"
                        w="32px"
                        h="32px"
                        p="0"
                        rounded="lg"
                        bg={page === pageNum ? "#0F172A" : "white"}
                        color={page === pageNum ? "white" : "gray.700"}
                        border="1px solid"
                        borderColor={page === pageNum ? "#0F172A" : "#E2E8F0"}
                        _hover={{ bg: page === pageNum ? "#0F172A" : "gray.50" }}
                        onClick={() => setPage(pageNum)}
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </Flex>

                <Button
                  size="sm"
                  variant="outline"
                  rightIcon={<ChevronRightIcon />}
                  isDisabled={page >= totalPages}
                  onClick={() => setPage(page + 1)}
                  rounded="lg"
                >
                  Next
                </Button>
              </Flex>
            )}
          </Box>
        </Flex>
      </Box>
    </Box>
  );
};

export default Products;
